import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import axios, {post, get} from 'axios';
const AppContext = createContext({});
const API_URL = 'https://datofy.cl/api';
import { getToken, saveToken } from "utils/auth";

const SESSION_CHECK_INTERVAL = 3 * 60 * 1000; // Verificar sesión cada 3 minutos

const AppContextProvider = ({ children }) => {
    const authProvider = useProvideAuth();
    const alumnosProvider = useProvideAlumnos();
    const customersProvider = useProvideCustomers();
    const sidebarProvider = useProvideSidebar();

    const [updateCount, setUpdateCount] = useState(0);

    const handleUpdateData = () => {
      setUpdateCount(updateCount + 1);
    };

    return (
        <AppContext.Provider
        value={{
            isAuthenticated: authProvider.isAuthenticated,
            authenticate: authProvider.authenticate,
            token: authProvider.token,
            handleLogout: authProvider.handleLogout,
            setIsAuthenticated: authProvider.setIsAuthenticated,

            sidebarCollapsed: sidebarProvider.sidebarCollapsed,
            toggleSidebarCollapsed: sidebarProvider.toggleSidebarCollapsed,

            alumnos_seleccionados: alumnosProvider.alumnos,
            setAlumnos: alumnosProvider.setAlumnos,
            alumnosTable: alumnosProvider.alumnosTable,
            getAlumnosTable: alumnosProvider.getAlumnosTable,
            alumnosGetParams: alumnosProvider.alumnosGetParams,
            setAlumnosGetParams: alumnosProvider.setAlumnosGetParams,

            groupSelected: customersProvider.groupSelected,
            setGroupSelected: customersProvider.setGroupSelected,
            customersTable: customersProvider.customersTable,
            setCustomersTable: customersProvider.setCustomersTable,
            customersGetParams: customersProvider.customersGetParams,
            setCustomersGetParams: customersProvider.setCustomersGetParams,
            ordersGetParams: customersProvider.ordersGetParams,
            setOrdersGetParams: customersProvider.setOrdersGetParams,

            updateCount,
            handleUpdateData
        }}
        >
            {children}
        </AppContext.Provider>
    );
};

function useProvideAuth() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);

  // Ref para evitar stale closures en interceptores y timers
  const handleLogoutRef = useRef(null);

  const handleLogout = () => {
    localStorage.clear();
    setIsAuthenticated(false);

    // getUserAuth/getAllSurveys (surveyService.js) cachean en variables de
    // módulo, no en estado de componente — sobreviven al logout porque esta
    // SPA nunca recarga la página al volver a /auth/signin. Sin limpiarlas
    // acá, el próximo login remonta Sidebar/Navbar (useUserAuth) pero
    // getUserAuth() seguía devolviendo el usuario/rol de la sesión anterior
    // desde userAuthCache (hasta 5 min) — quedaba "colgada" la sesión previa
    // hasta que alguien recargaba manualmente la página.
    import('../services/surveyService').then(({ clearUserAuthCache, clearSurveysCache }) => {
      clearUserAuthCache?.();
      clearSurveysCache?.();
    }).catch(() => {});
  };

  // Mantener ref actualizada con la función más reciente
  handleLogoutRef.current = handleLogout;

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsAuthenticated(token ? true : false);
    setToken(token);
  }, []);

  // ── Capa 1: Interceptor global axios ─────────────────────────────────────
  // Cualquier respuesta 401 (token inválido o usuario suspendido) → logout inmediato
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      response => response,
      error => {
        const status = error.response?.status;
        const url = error.config?.url || '';
        // Ignorar 401 del propio login para no crear un loop
        if (status === 401 && !url.includes('/login') && !url.includes('/auth/login')) {
          handleLogoutRef.current?.();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []); // solo al montar

  // ── Capa 2: Watcher periódico de sesión ───────────────────────────────────
  // Cada 3 minutos verifica activamente si el usuario sigue activo en el servidor.
  // Detecta suspensiones aunque el usuario no haga ninguna acción.
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkSession = async () => {
      try {
        // Importación dinámica para evitar dependencia circular
        const { getUserAuth } = await import('../services/surveyService');
        await getUserAuth(true); // force refresh, omite cache
      } catch (err) {
        if (err.response?.status === 401) {
          handleLogoutRef.current?.();
        }
      }
    };

    const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL);
    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // ── Capa 3: Sincronización entre pestañas ─────────────────────────────────
  // Si otra pestaña hace logout (borra el token), esta también cierra sesión
  useEffect(() => {
    const onStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue && isAuthenticated) {
        handleLogoutRef.current?.();
      }
    };
    window.addEventListener('storage', onStorageChange);
    return () => window.removeEventListener('storage', onStorageChange);
  }, [isAuthenticated]);

  return {
    isAuthenticated,
    setIsAuthenticated,
    token,
    setToken,
    handleLogout,
  };
}

// Sidebar izquierdo (Sidebar/index.js) colapsado a riel de íconos — global
// (no solo Scanner) porque Sidebar es un componente compartido por TODA la
// app, montado como hermano de MainPanel en layouts/Admin.js, no un hijo de
// ninguna pantalla en particular. Vive en AppContext (ya envuelve toda la
// app desde index.js) en vez de un context nuevo, para no agregar otro
// Provider más. Persistido para que la preferencia no se pierda al navegar.
const SIDEBAR_COLLAPSED_KEY = 'admin-sidebar-collapsed';

function useProvideSidebar() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  return { sidebarCollapsed, toggleSidebarCollapsed };
}

function useProvideAlumnos() {
  const [alumnos, setAlumnos] = useState([]);
  const [alumnosTable, setAlumnosTable] = useState([]);
  const [alumnosGetParams, setAlumnosGetParams] = React.useState();
  const [tableRef, setTableRef] = React.useState(null);

  const getAlumnosTable = (params) => {
    return get(`${API_URL}/alumnos`, {
      headers,
      params: params || alumnosGetParams,
    })
      .then((response) => {
        setAlumnosTable(response.data);
        return response.data;
      });
  }
  
  return {
    alumnos,
    setAlumnos,
    alumnosTable,
    alumnosGetParams,
    setAlumnosGetParams,
    getAlumnosTable,
  }
}

function useProvideCustomers() {
  const [groupSelected, setGroupSelected] = useState({});
  const [customersTable, setCustomersTable] = useState([]);
  const [customersGetParams, setCustomersGetParams] = useState({per_page: 10, page: 1});
  const [ordersGetParams, setOrdersGetParams] = useState({
    orderby: 'modified',
    order: 'desc',
  });

  // Cargar los datos de LocalStorage al inicio
  useEffect(() => {
      const selectedGroup = JSON.parse(localStorage.getItem('groupSelected'));
      const tableCustomers = JSON.parse(localStorage.getItem('customersTable'));
      const getParamsCustomers = JSON.parse(localStorage.getItem('customersGetParams'));
      const getParamsOrders = JSON.parse(localStorage.getItem('ordersGetParams'));

      if (selectedGroup) { // Aquí se debe usar selectedGroup en lugar de groupSelected
        console.log('selectedGroup', selectedGroup)
        setGroupSelected(selectedGroup);
      }

      if (tableCustomers) {
          setCustomersTable(tableCustomers);
      }

      if (getParamsCustomers) {
          setCustomersGetParams(getParamsCustomers);
      }

      // if (getParamsOrders) {
      //   setOrdersGetParams(getParamsOrders);
      // }
  }, []);

  // Actualizar los datos en LocalStorage cuando cambien
  useEffect(() => {
    localStorage.setItem('groupSelected', JSON.stringify(groupSelected));
    localStorage.setItem('customersTable', JSON.stringify(customersTable));
    localStorage.setItem('ordersGetParams', JSON.stringify(ordersGetParams));
  }, [groupSelected, customersTable, customersGetParams, ordersGetParams]);

  return {
    groupSelected,
    setGroupSelected,
    customersTable,
    setCustomersTable,
    customersGetParams,
    setCustomersGetParams,
    ordersGetParams,
    setOrdersGetParams
  }
}


const useAppContext = () => useContext(AppContext);

export { AppContext as default, AppContextProvider, useAppContext };